import * as yup from 'yup'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import utc from 'dayjs/plugin/utc'
import { PRODUCT_TYPE } from 'types/bundleProduct'
import { VAT_PERCENTAGES } from 'utils/constants'

dayjs.extend(isSameOrAfter)
dayjs.extend(utc)

const uniqueArray = (message: string) =>
  yup
    .array()
    .of(yup.string().required())
    .test('unique', message, (value) => {
      if (!value) return true
      return value.length === new Set(value).size
    })

// The shared Dropdown component always prepends a "--- NONE ---" (value
// "NONE") placeholder option, and defaults to it when no defaultValue is
// passed in — as is the case on this create form. Plain `.required()` would
// let that sentinel string through since it's non-empty, silently skipping
// the field. Explicitly reject it so the user has to make a real selection.
const requiredSelectField = (label: string) =>
  yup
    .string()
    .trim()
    .required(`${label} is required`)
    .notOneOf(['NONE'], `${label} is required`)

const priceField = (label: string) =>
  yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? undefined : value,
    )
    .typeError(`${label} must be a number`)
    .positive(`${label} must be greater than zero`)

const nullableDateField = () =>
  yup
    .string()
    .nullable()
    .default(null)
    .transform((value) => (value === '' || value === undefined ? null : value))
    // Cast a plain `YYYY-MM-DD` date-input value to a full ISO 8601 timestamp
    // on submit, matching the backend's expected pricing payload shape.
    // Interpreted as UTC midnight so the calendar date doesn't shift
    // depending on the browser's local timezone.
    .transform((value) => (value ? dayjs.utc(value).toISOString() : value))

const quantityField = () =>
  yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? undefined : value,
    )
    .typeError('Quantity must be a whole number')
    .integer('Quantity must be a whole number')
    .min(1, 'Quantity must be a positive integer')
    .default(1)

const bundleItemsSchema = yup
  .array()
  .of(
    yup.object({
      catalogue: yup.string().required('Product is required'),
      price: priceField('Price').required('Price is required'),
      specialPrice: priceField('Special Price'),
      quantity: quantityField(),
    }),
  )
  .test(
    'unique-product',
    'Duplicate child products are not allowed',
    (value) => {
      if (!value) return true
      const ids = value.map((v) => v.catalogue).filter(Boolean)
      return ids.length === new Set(ids).size
    },
  )

const dateRangeTest = (
  field: ReturnType<typeof nullableDateField>,
  startKey: string,
  endKey: string,
  isEnd: boolean,
) =>
  field.test(
    isEnd ? 'is-after-start' : 'is-before-end',
    isEnd
      ? 'End date must be on or after the start date'
      : 'Start date must be on or before the end date',
    function () {
      const start = this.parent[startKey]
      const end = this.parent[endKey]
      if (!start || !end) return true
      return dayjs(end).isSameOrAfter(dayjs(start))
    },
  )

// price is required; the other 7 fields are optional and independent of one
// another. Numeric fields are positive-number inputs, date fields accept
// empty/null and are cast to ISO 8601 on submit. Shared by the Create form
// and the Master Data update form's bundle pricing section.
export const pricingSchema = yup.object({
  price: priceField('Price').required('Price is required'),
  onlinePrice: priceField('Online Price'),
  specialPrice: priceField('Special Price'),
  specialPriceStartDate: dateRangeTest(
    nullableDateField(),
    'specialPriceStartDate',
    'specialPriceEndDate',
    false,
  ),
  specialPriceEndDate: dateRangeTest(
    nullableDateField(),
    'specialPriceStartDate',
    'specialPriceEndDate',
    true,
  ),
  slashPrice: priceField('Slash Price'),
  slashPriceStartDate: dateRangeTest(
    nullableDateField(),
    'slashPriceStartDate',
    'slashPriceEndDate',
    false,
  ),
  slashPriceEndDate: dateRangeTest(
    nullableDateField(),
    'slashPriceStartDate',
    'slashPriceEndDate',
    true,
  ),
})

// Cross-field check between the bundle's own root pricing and the sum of its
// child items — both fields live as separate top-level siblings (`pricing`
// and `bundleItems`), so this can't be expressed inside either field's own
// schema and instead runs as a whole-object test with access to both.
//
// - Normal total: sum(child.price * child.quantity) must equal
//   pricing.onlinePrice if set, else pricing.price.
// - Special total: only enforced when pricing.specialPrice is set. Every
//   child must then have its own specialPrice (no falling back to price),
//   and sum(child.specialPrice * child.quantity) must equal
//   pricing.specialPrice exactly.
const bundleTotalsMatch = <T extends yup.ObjectSchema<any>>(schema: T) =>
  schema
    .test(
      'bundle-total-matches-children',
      'Sum of child product prices does not match the bundle total price',
      function (value: any) {
        const items = value?.bundleItems
        if (!Array.isArray(items) || items.length === 0) return true

        const usingOnlinePrice =
          value?.pricing?.onlinePrice !== undefined &&
          value?.pricing?.onlinePrice !== null
        const rootTotal = usingOnlinePrice
          ? value?.pricing?.onlinePrice
          : value?.pricing?.price
        if (rootTotal === undefined || rootTotal === null) return true

        const childTotal = items.reduce(
          (sum: number, item: { price?: number; quantity?: number }) =>
            sum + (item.price ?? 0) * (item.quantity ?? 1),
          0,
        )

        if (Math.abs(childTotal - rootTotal) < 0.01) return true

        const rootFieldLabel = usingOnlinePrice ? 'Online Price' : 'Price'

        return this.createError({
          path: `${this.path}.pricing.price`,
          message: `Sum of child product prices (${childTotal}) must equal the bundle total ${rootFieldLabel} (${rootTotal})`,
        })
      },
    )
    .test(
      'bundle-special-total-matches-children',
      'Sum of child special prices does not match the bundle special price',
      function (value: any) {
        const rootSpecial = value?.pricing?.specialPrice
        if (rootSpecial === undefined || rootSpecial === null) return true

        const items = value?.bundleItems
        if (!Array.isArray(items) || items.length === 0) return true

        const missingSpecialIndexes = items.reduce(
          (
            indexes: number[],
            item: { specialPrice?: number },
            index: number,
          ) => {
            if (item.specialPrice === undefined || item.specialPrice === null) {
              indexes.push(index)
            }
            return indexes
          },
          [],
        )
        if (missingSpecialIndexes.length > 0) {
          const requiredMessage =
            'Special price is required when the bundle has a special price'
          return new yup.ValidationError([
            this.createError({
              path: `${this.path}.pricing.specialPrice`,
              message:
                'Every child product must have a special price set when the bundle has a special price',
            }),
            ...missingSpecialIndexes.map(
              (index: number) =>
                new yup.ValidationError(
                  requiredMessage,
                  undefined,
                  `${this.path}.bundleItems[${index}].specialPrice`,
                ),
            ),
          ])
        }

        const childSpecialTotal = items.reduce(
          (sum: number, item: { specialPrice?: number; quantity?: number }) =>
            sum + (item.specialPrice ?? 0) * (item.quantity ?? 1),
          0,
        )

        if (Math.abs(childSpecialTotal - rootSpecial) < 0.01) return true

        return this.createError({
          path: `${this.path}.pricing.specialPrice`,
          message: `Sum of child special prices (${childSpecialTotal}) must equal the bundle special price (${rootSpecial})`,
        })
      },
    )

export const createProductSchema = bundleTotalsMatch(
  yup.object({
    productType: yup
      .mixed<(typeof PRODUCT_TYPE)[keyof typeof PRODUCT_TYPE]>()
      .oneOf(Object.values(PRODUCT_TYPE))
      .default(PRODUCT_TYPE.BUNDLE),
    description: yup.string().trim().required('Description is required'),
    dept: requiredSelectField('Department'),
    section: requiredSelectField('Section'),
    family: requiredSelectField('Family'),
    subFamily: yup.string().trim().required('Sub Family is required'),
    uom: yup.string().trim().required('UOM is required'),
    vatClass: yup
      .string()
      .oneOf(Object.keys(VAT_PERCENTAGES), 'Select a valid VAT Class')
      .required('VAT Class is required'),
    activeOnline: yup.boolean(),
    hideAggregators: uniqueArray('Hide Aggregators must be unique'),
    specialCategories: uniqueArray('Special Categories must be unique'),
    classifications: yup.array().of(yup.string().required()),
    erpBrand: yup.string(),
    erpCoo: yup.string(),
    erpMarketingCode: yup.string(),
    image: yup.string(),
    isDisable: yup.boolean(),
    pricing: pricingSchema,
    onlineDesc: yup.string(),
    onlinePackInfo: yup.string(),
    qtyPerUom: yup.string(),
    quantityStep: yup
      .number()
      .typeError('Quantity Step must be a number')
      .positive('Quantity Step must be greater than zero'),
    uomPackagingInfo: yup.string(),
    bundleItems: bundleItemsSchema
      .required('At least 2 child products are required')
      .min(2, 'At least 2 child products are required'),
  }),
)

export type CreateProductFormFields = yup.InferType<typeof createProductSchema>

// Validates only bundleItems/pricing on the Master Data update form, which
// otherwise has no yup resolver — both are only required when productType is
// BUNDLE, so this stays a no-op for normal products. Without the `pricing`
// `.when()` here, a normal product with no `catalogue.pricing` (the common
// case — pricing is bundle-only) would fail pricingSchema's required `price`
// field on every submit, and since none of the bundle-only UI is rendered for
// normal products, that error would never surface anywhere — the form would
// just silently refuse to submit.
export const updateMasterDataBundleSchema = bundleTotalsMatch(
  yup.object({
    productType: yup.mixed<(typeof PRODUCT_TYPE)[keyof typeof PRODUCT_TYPE]>(),
    pricing: pricingSchema.when('productType', {
      is: PRODUCT_TYPE.BUNDLE,
      then: (schema) => schema,
      otherwise: (schema) => schema.strip(),
    }),
    bundleItems: bundleItemsSchema.when('productType', {
      is: PRODUCT_TYPE.BUNDLE,
      then: (schema) =>
        schema
          .required('At least 2 child products are required')
          .min(2, 'At least 2 child products are required'),
      otherwise: (schema) => schema.strip(),
    }),
    // Safety net behind the update form's live-filtered dropdown (see
    // masterDataUpdateForm.tsx's selectedActivatedStores) - In Stock Stores
    // may only contain stores also present in Activated Stores.
    inStockStores: yup
      .array()
      .of(yup.string().required())
      .test(
        'in-stock-stores-subset-of-activated',
        'In Stock Stores can only contain stores selected in Activated Stores',
        (value, context) => {
          if (!value?.length) return true

          const activatedStores: string[] =
            context.parent?.activatedStores || []
          return value.every((store) => activatedStores.includes(store))
        },
      ),
  }),
)
